<template>
  <div class="modal-backdrop-custom">
    <div class="modal-dialog-custom">
      <div class="card border-0 shadow-lg">
        <form @submit.prevent="submit">
          <div
            class="card-header bg-white d-flex justify-content-between align-items-center"
          >
            <div>
              <h5 class="mb-0">
                {{ isEditing ? "Edit Beneficiary" : "Add Beneficiary" }}
              </h5>

              <small v-if="guardian" class="text-muted">
                Guardian: {{ guardian.name }}
              </small>
            </div>

            <button
              type="button"
              class="btn-close"
              :disabled="saving"
              @click="$emit('close')"
            ></button>
          </div>

          <div class="card-body">
            <div v-if="error" class="alert alert-danger">
              {{ error }}
            </div>

            <div class="row g-3">
              <div class="col-md-8">
                <label class="form-label">
                  Beneficiary Name
                </label>

                <input
                  v-model.trim="form.name"
                  type="text"
                  class="form-control"
                  maxlength="255"
                  required
                />
              </div>

              <div class="col-md-4">
                <label class="form-label">
                  Relationship
                </label>

                <input
                  v-model.trim="form.relationship"
                  type="text"
                  class="form-control"
                  maxlength="255"
                  placeholder="Child, dependent..."
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  Date of Birth
                </label>

                <input
                  v-model="form.date_of_birth"
                  type="date"
                  class="form-control"
                  :max="today"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  Joined Date
                </label>

                <input
                  v-model="form.joined_at"
                  type="date"
                  class="form-control"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  Registration Fee Amount
                </label>

                <input
                  v-model="form.registration_fee_amount"
                  type="number"
                  class="form-control"
                  min="0"
                  step="0.01"
                  :disabled="!form.registration_fee_required"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  Registration Fee Status
                </label>

                <select
                  v-model="form.registration_fee_status"
                  class="form-select"
                  :disabled="!form.registration_fee_required"
                >
                  <option value="pending">
                    Pending
                  </option>

                  <option value="paid">
                    Paid
                  </option>

                  <option value="waived">
                    Waived
                  </option>

                  <option value="not_applicable">
                    Not applicable
                  </option>
                </select>
              </div>

              <div class="col-12">
                <div class="form-check">
                  <input
                    id="beneficiary-registration-fee"
                    v-model="form.registration_fee_required"
                    type="checkbox"
                    class="form-check-input"
                    @change="onFeeRequiredChanged"
                  />

                  <label
                    class="form-check-label"
                    for="beneficiary-registration-fee"
                  >
                    Registration fee is required
                  </label>
                </div>
              </div>

              <div class="col-12">
                <label class="form-label">
                  Registration Note
                </label>

                <textarea
                  v-model.trim="form.registration_note"
                  class="form-control"
                  rows="2"
                  maxlength="255"
                ></textarea>
              </div>

              <div class="col-12">
                <div class="form-check">
                  <input
                    id="beneficiary-active"
                    v-model="form.is_active"
                    type="checkbox"
                    class="form-check-input"
                  />

                  <label
                    class="form-check-label"
                    for="beneficiary-active"
                  >
                    Beneficiary is active
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="card-footer bg-white text-end">
            <button
              type="button"
              class="btn btn-light me-2"
              :disabled="saving"
              @click="$emit('close')"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary"
              :disabled="saving"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
              ></span>

              {{
                saving
                  ? "Saving..."
                  : isEditing
                    ? "Update Beneficiary"
                    : "Create Beneficiary"
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createBeneficiary,
  updateBeneficiary,
} from "@/services/beneficiaries";

export default {
  name: "BeneficiaryModal",

  props: {
    guardian: {
      type: Object,
      required: true,
    },

    initial: {
      type: Object,
      default: null,
    },
  },

  emits: [
    "close",
    "saved",
  ],

  data() {
    return {
      saving: false,
      error: "",

      form: {
        name: "",
        relationship: "",
        date_of_birth: "",
        joined_at: "",
        is_active: true,
        registration_fee_required: true,
        registration_fee_amount: "10000.00",
        registration_fee_status: "pending",
        registration_note: "",
      },
    };
  },

  computed: {
    isEditing() {
      return Boolean(this.initial?.id);
    },

    today() {
      return new Date()
        .toISOString()
        .slice(0, 10);
    },
  },

  created() {
    this.populateForm();
  },

  methods: {
    dateValue(value) {
      if (!value) {
        return "";
      }

      return String(value).slice(0, 10);
    },

    populateForm() {
      if (!this.initial) {
        return;
      }

      this.form = {
        name: this.initial.name || "",
        relationship:
          this.initial.relationship || "",
        date_of_birth: this.dateValue(
          this.initial.date_of_birth
        ),
        joined_at: this.dateValue(
          this.initial.joined_at
        ),
        is_active:
          this.initial.is_active !== false,
        registration_fee_required:
          this.initial.registration_fee_required !== false,
        registration_fee_amount:
          this.initial.registration_fee_amount ??
          "10000.00",
        registration_fee_status:
          this.initial.registration_fee_status ||
          "pending",
        registration_note:
          this.initial.registration_note || "",
      };
    },

    onFeeRequiredChanged() {
      if (!this.form.registration_fee_required) {
        this.form.registration_fee_status =
          "not_applicable";

        this.form.registration_fee_amount = "0";
      } else {
        if (
          this.form.registration_fee_status ===
          "not_applicable"
        ) {
          this.form.registration_fee_status =
            "pending";
        }

        if (
          Number(
            this.form.registration_fee_amount
          ) <= 0
        ) {
          this.form.registration_fee_amount =
            "10000.00";
        }
      }
    },

    buildPayload() {
      return {
        guardian_user_id:
          Number(this.guardian.id),

        name: this.form.name.trim(),

        relationship:
          this.form.relationship?.trim() ||
          null,

        date_of_birth:
          this.form.date_of_birth || null,

        joined_at:
          this.form.joined_at || null,

        is_active:
          Boolean(this.form.is_active),

        registration_fee_required:
          Boolean(
            this.form.registration_fee_required
          ),

        registration_fee_amount:
          Number(
            this.form.registration_fee_amount || 0
          ),

        registration_fee_status:
          this.form.registration_fee_required
            ? this.form.registration_fee_status
            : "not_applicable",

        registration_note:
          this.form.registration_note?.trim() ||
          null,
      };
    },

    async submit() {
      if (this.saving) {
        return;
      }

      if (!this.form.name.trim()) {
        this.error =
          "Beneficiary name is required.";

        return;
      }

      this.saving = true;
      this.error = "";

      try {
        const payload = this.buildPayload();

        const response = this.isEditing
          ? await updateBeneficiary(
              this.initial.id,
              payload
            )
          : await createBeneficiary(payload);

        this.$emit(
          "saved",
          response.data?.data
        );
      } catch (error) {
        const validationErrors =
          error?.response?.data?.errors;

        if (validationErrors) {
          this.error = Object.values(
            validationErrors
          )
            .flat()
            .join(" ");
        } else {
          this.error =
            error?.response?.data?.message ||
            "Failed to save beneficiary.";
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
  overflow-y: auto;
}

.modal-dialog-custom {
  width: 100%;
  max-width: 760px;
}
</style>